<script setup lang="ts">
import { z } from 'zod';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';

interface BeforeInstallPromptEvent extends Event {
	prompt: () => Promise<void>;
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

definePageMeta({
	layout: 'default',
});

const route = useRoute();
const { login, isAuthenticated } = useAuth();

// Se già autenticato, reindirizza all'area riservata
if (isAuthenticated.value) {
	await navigateTo('/area-riservata');
}

const loginSchema = z.object({
	email: z.string().email('Email non valida'),
	password: z.string().min(1, 'Password obbligatoria'),
});

const { handleSubmit, isSubmitting, errors, defineField } = useForm({
	validationSchema: toTypedSchema(loginSchema),
});

const [email, emailAttrs] = defineField('email');
const [password, passwordAttrs] = defineField('password');

const router = useRouter();
const errorMessage = ref<string | null>(null);
const deferredInstallPrompt = ref<BeforeInstallPromptEvent | null>(null);
const showManualInstallSteps = ref(false);

const isPwaMode = ref(false);
const isMobileDevice = ref(false);
const isIOS = ref(false);
const isAndroid = ref(false);
const browserName = ref('browser');

const canInstallDirectly = computed(() => deferredInstallPrompt.value !== null);
const shouldShowInstallBanner = computed(() => isMobileDevice.value && !isPwaMode.value);
const installButtonLabel = computed(() => canInstallDirectly.value ? 'Aggiungi alla Home' : 'Come aggiungere alla Home');
const installBannerText = computed(() => {
	if (isIOS.value) {
		return 'Per un accesso rapido all\'area riservata, aggiungi WeFocus alla schermata Home del tuo iPhone/iPad.';
	}
	if (isAndroid.value) {
		return 'Installa WeFocus sulla Home del tuo telefono per aprire l\'area riservata come app.';
	}
	return 'Aggiungi WeFocus alla Home per aprire l\'area riservata come app.';
});

function detectMobileDevice() {
	if (import.meta.server) {
		return false;
	}

	const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
	const narrowViewport = window.matchMedia('(max-width: 1024px)').matches;
	const mobileUserAgent = /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);

	return mobileUserAgent || (hasCoarsePointer && narrowViewport);
}

function detectBrowserName(ua: string) {
	if (/samsungbrowser/i.test(ua)) {
		return 'Samsung Internet';
	}
	if (/edg/i.test(ua)) {
		return 'Edge';
	}
	if (/opr|opera/i.test(ua)) {
		return 'Opera';
	}
	if (/firefox|fxios/i.test(ua)) {
		return 'Firefox';
	}
	if (/crios|chrome/i.test(ua)) {
		return 'Chrome';
	}
	if (/safari/i.test(ua)) {
		return 'Safari';
	}
	return 'browser';
}

async function onInstallClick() {
	if (deferredInstallPrompt.value) {
		await deferredInstallPrompt.value.prompt();
		await deferredInstallPrompt.value.userChoice;
		deferredInstallPrompt.value = null;
		return;
	}

	showManualInstallSteps.value = !showManualInstallSteps.value;
}

onMounted(() => {
	const ua = navigator.userAgent;
	const uaLower = ua.toLowerCase();

	isPwaMode.value = useIsPwa();
	isMobileDevice.value = detectMobileDevice();
	isIOS.value = /iphone|ipad|ipod/.test(uaLower);
	isAndroid.value = /android/.test(uaLower);
	browserName.value = detectBrowserName(uaLower);

	const onBeforeInstallPrompt = (event: Event) => {
		event.preventDefault();
		deferredInstallPrompt.value = event as BeforeInstallPromptEvent;
	};

	const onAppInstalled = () => {
		isPwaMode.value = true;
		deferredInstallPrompt.value = null;
		showManualInstallSteps.value = false;
	};

	window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt as EventListener);
	window.addEventListener('appinstalled', onAppInstalled);

	onBeforeUnmount(() => {
		window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt as EventListener);
		window.removeEventListener('appinstalled', onAppInstalled);
	});
});

const onSubmit = handleSubmit(async (values) => {
	console.log('Form submitted with values:', { email: values.email, password: '***' });
	errorMessage.value = null;
	
	try {
		console.log('Calling login function...');
		const result = await login(values.email, values.password);
		console.log('Login result:', { success: result.success, error: result.error });
		
		if (result.success) {
			console.log('Login successful, redirecting...');
			// Attendi che lo stato sia aggiornato
			await nextTick();
			// Attendi un po' di più per assicurarsi che lo stato reattivo sia propagato
			await new Promise(resolve => setTimeout(resolve, 100));
			
			const { isAuthenticated, canAccessReservedArea } = useAuth();
			console.log('Auth state after login:', { 
				isAuthenticated: isAuthenticated.value, 
				canAccessReservedArea: canAccessReservedArea.value 
			});
			
			const redirect = (route.query.redirect as string) || '/area-riservata';
			console.log('Navigating to:', redirect);
			
			try {
				// Usa window.location per forzare un reload completo se necessario
				await navigateTo(redirect, { external: false });
			} catch (navError: any) {
				console.error('Navigation error:', navError);
				// Se la navigazione fallisce, prova con router.push
				try {
					await router.push(redirect);
				} catch (pushError: any) {
					console.error('Router.push also failed:', pushError);
					// Ultimo tentativo: usa window.location
					if (process.client) {
						window.location.href = redirect;
					}
				}
			}
		} else {
			console.error('Login failed:', result.error);
			errorMessage.value = result.error || 'Errore durante il login';
		}
	} catch (error: any) {
		console.error('Error in onSubmit:', error);
		errorMessage.value = error.message || 'Errore durante il login';
	}
});
</script>

<template>
	<div class="flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
		<div class="max-w-md w-full space-y-8">
			<div>
				<h2 class="mt-6 text-left text-3xl font-extrabold text-gray-900">
					Accedi
				</h2>
			</div>
			
			<form class="mt-8 space-y-6" @submit.prevent="onSubmit">
				<div class="space-y-4">
					<div>
						<label for="email" class="block text-sm font-medium text-gray-700">
							Email
						</label>
						<input
							id="email"
							v-model="email"
							v-bind="emailAttrs"
							type="email"
							autocomplete="email"
							class="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
							placeholder="La tua email"
						/>
						<p v-if="errors.email" class="mt-1 text-sm text-red-600">
							{{ errors.email }}
						</p>
					</div>
					<div>
						<label for="password" class="block text-sm font-medium text-gray-700">
							Password
						</label>
						<input
							id="password"
							v-model="password"
							v-bind="passwordAttrs"
							type="password"
							autocomplete="current-password"
							class="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
							placeholder="La tua password"
						/>
						<p v-if="errors.password" class="mt-1 text-sm text-red-600">
							{{ errors.password }}
						</p>
					</div>
				</div>

				<div>
					<Button
						type="submit"
						:disabled="isSubmitting"
						variant="default"
						class="w-full uppercase"
						@click="(e) => { console.log('Button clicked, isSubmitting:', isSubmitting); }"
					>
						<span v-if="!isSubmitting">ACCEDI</span>
						<span v-else>Accesso in corso...</span>
					</Button>
				</div>
				
				<div v-if="errorMessage" class="rounded-md bg-red-50 p-4">
					<div class="flex">
						<div class="flex-shrink-0">
							<svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
								<path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
							</svg>
						</div>
						<div class="ml-3">
							<p class="text-sm font-medium text-red-800">{{ errorMessage }}</p>
						</div>
					</div>
				</div>
				
				<div class="text-left">
					<NuxtLink
						to="/forgot-password"
						class="text-sm font-medium text-black hover:text-gray-800"
					>
						Hai dimenticato la password?
					</NuxtLink>
				</div>
			</form>

			<div
				v-if="shouldShowInstallBanner"
				class="border border-gray-200 bg-white p-4 text-left shadow-sm"
			>
				<p class="text-sm font-semibold text-gray-900">
					Apri più velocemente WeFocus
				</p>
				<p class="mt-1 text-sm text-gray-600">
					{{ installBannerText }}
				</p>
				<div class="mt-3">
					<Button
						type="button"
						variant="outline"
						class="w-full uppercase"
						@click="onInstallClick"
					>
						{{ installButtonLabel }}
					</Button>
				</div>

				<div
					v-if="showManualInstallSteps && !canInstallDirectly"
					class="mt-3 rounded-md bg-gray-50 p-3 text-sm text-gray-700"
				>
					<ol v-if="isIOS" class="list-decimal space-y-1 pl-5">
						<li>Apri questa pagina in Safari.</li>
						<li>Tocca il pulsante Condividi (quadrato con freccia in alto).</li>
						<li>Tocca "Mostra tutti" (freccia in basso).</li>
						<li>Seleziona "Aggiungi a schermata Home".</li>
						<li>Conferma con "Aggiungi".</li>
					</ol>
					<ol v-else class="list-decimal space-y-1 pl-5">
						<li>Apri il menu del browser (di solito icona con 3 puntini).</li>
						<li>
							Tocca "Installa app" oppure "Aggiungi a schermata Home"
							<span v-if="browserName !== 'browser'">su {{ browserName }}</span>.
						</li>
						<li>Conferma l'aggiunta alla Home.</li>
					</ol>
				</div>
			</div>
		</div>
	</div>
</template>

